import React from 'react';
import { Button, Row, Col, Tooltip } from 'antd';
import { EyeOutlined, StopOutlined } from '@ant-design/icons';
import {
  CustomModal,
  CustomTable,
  GeneralContainer,
} from '../../../../components';
import SearchRow from '../components/SearchFilters';
import { EntryRequestResponse } from '../../types/entry-response';
import { ShareResponse } from '../../../../share';
import useApproveRequestsFormik from '../../../../utils/validators/approve-request/approve-request-validator';
import CancelRequest from '../../../ApproveRequests/components/components/ByRoles/CancelRequest';
import CustomAlert from '../../../../components/CustomAlert/CustomAlert';

interface EntryRequestsViewProps {
  data?: EntryRequestResponse;
  onViewCreate: () => void;
  onPagination: (page: number, pageSize: number) => void;
  onViewDetail: (id: string) => void;
  formik: any;
  states?: ShareResponse[];
  isLoading: boolean;
  onResetFilters: () => void;
  onCancelRequest: (id: string, reason: string) => Promise<void>;
  loadingData?: boolean;
}

const EntryRequestsView: React.FC<EntryRequestsViewProps> = ({
  data,
  onViewCreate,
  onViewDetail,
  onPagination,
  formik,
  states,
  isLoading,
  onResetFilters,
  onCancelRequest,
  loadingData,
}) => {
  const [modalCancel, setModalCancel] = React.useState(false);
  const [requestId, setRequestId] = React.useState('');

  const formikCancelRequest = useApproveRequestsFormik({ onSubmit: () => {} });

  const onConfirmModal = () => {
    onCancelRequest(requestId, formikCancelRequest.values.reason).finally(
      () => {
        setModalCancel(false);
        formikCancelRequest.resetForm();
      }
    );
  };

  const columns = [
    {
      title: 'Número ticket',
      dataIndex: 'ticket',
      key: 'ticket',
      sorter: (a: any, b: any) => a.ticket.localeCompare(b.ticket),
      align: 'center',
    },
    {
      title: 'Tipo ticket',
      dataIndex: 'ticket_type',
      key: 'ticket_type',
      sorter: (a: any, b: any) => a.ticket_type.localeCompare(b.ticket_type),
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Tipo de bien',
      dataIndex: 'item_type',
      key: 'item_type',
      sorter: (a: any, b: any) => a.item_type.localeCompare(b.item_type),
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Fecha de solicitud',
      dataIndex: 'query_date',
      key: 'query_date',
      sorter: (a: any, b: any) => a.query_date.localeCompare(b.query_date),
      align: 'center',
    },
    {
      title: 'Regional',
      dataIndex: 'regional',
      key: 'regional',
      sorter: (a: any, b: any) => a.regional.localeCompare(b.regional),
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Seccional',
      dataIndex: 'sectional',
      key: 'sectional',
      sorter: (a: any, b: any) => a.sectional.localeCompare(b.sectional),
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Estado de solicitud',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: string) => {
        return <CustomAlert status={status} />
      },
    },
    {
      title: 'Solicitante',
      dataIndex: 'requester',
      key: 'requester',
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Observaciones',
      dataIndex: 'observations',
      key: 'observations',
      align: 'center',
      width: 300,
      render: (text: string) => {
        return (
          <Tooltip title={text || 'Sin registro'}>
            <div
              style={{
                maxWidth: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: '0 auto',
              }}
            >
              {text || 'Sin registro'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_: any, record: any) => (
        <>
          <Tooltip title="Ver solicitud">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetail(record)}
            />
          </Tooltip>
          {([8] as number[]).includes(record?.status_id) && (
            <Tooltip title="Anular solicitud">
              <Button
                type="link"
                icon={<StopOutlined />}
                onClick={() => {
                  setRequestId(record?.petition_id);
                  setModalCancel(true);
                }}
              />
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <GeneralContainer
      title="Solicitudes de entrada"
      actionLabel="Crear solicitud"
      onActionClick={onViewCreate}
      isLoading={loadingData}
    >
      {/* Filtros */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24}>
          <SearchRow
            formik={formik}
            states={states}
            onCancel={onResetFilters}
            isLoading={isLoading}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={24}>
          <CustomTable
            loading={isLoading}
            columns={columns as any}
            dataSource={data?.data?.map((entry) => ({
              ...entry,
              id: Number(entry.id) || -1,
            }))}
            paginationConfig={{
              current: data?.current_page,
              pageSize: data?.per_page,
              total: data?.total,
              onChange: onPagination,
            }}
            scroll={{ x: 'max-content' }}
          />
        </Col>
      </Row>

      <CustomModal
        title=""
        labelSubmit="Aceptar"
        open={modalCancel}
        width={1000}
        onCancel={() => setModalCancel(false)}
        onSave={onConfirmModal}
        disabledBtn={!formikCancelRequest.values.reason}
      >
        <CancelRequest formik={formikCancelRequest} />
      </CustomModal>
    </GeneralContainer>
  );
};

export default EntryRequestsView;
